import React, { useId, useState } from 'react';
import Button from '../ui/Button';
import SearchableSelect from '../ui/SearchableSelect';
import { errorMessageOf } from '../../utils/apiErrors';
import { ROLE_DESCRIPTIONS, ROLE_LABELS, ROLE_ORDER } from '../../utils/roles';
import type { UserRole } from '../../types/User';
import type { Person } from '../../types/People';

interface GiveLoginPanelProps {
  person: Person;
  onCreate: (role: UserRole, email?: string) => Promise<void>;
  onCancel: () => void;
}

/**
 * Owner only: pick a role for a person and create their login. A login needs an
 * e-mail, so it is asked for here when the person has none. The role covers the
 * whole workspace.
 */
const GiveLoginPanel: React.FC<GiveLoginPanelProps> = ({ person, onCreate, onCancel }) => {
  const uid = useId();
  const needsEmail = !person.email;
  const [role, setRole] = useState<UserRole>('planner');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || (needsEmail && !email.trim())) return;
    setSaving(true);
    setError(null);
    try {
      await onCreate(role, needsEmail ? email.trim().toLowerCase() : undefined);
    } catch (err) {
      setError(errorMessageOf(err, "Couldn't create the login. Please try again."));
      setSaving(false);
    }
  };

  return (
    <form className="people-panel" onSubmit={submit} noValidate>
      {needsEmail && (
        <div className="form-group">
          <label htmlFor={`${uid}-email`}>Email <span className="form-required">*</span></label>
          <input id={`${uid}-email`} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="They sign in with this" autoFocus />
        </div>
      )}
      <div className="form-group">
        <span className="form-label">Role</span>
        <SearchableSelect
          value={role}
          onChange={value => setRole(value as UserRole)}
          options={ROLE_ORDER.map(r => ({ value: r, label: ROLE_LABELS[r] }))}
        />
      </div>
      <p className="form-hint people-panel__hint">
        {ROLE_DESCRIPTIONS[role]}. The role covers the whole workspace.
        {role === 'viewer' && ' To hide money from a viewer, edit the login in the list below once it exists.'}
      </p>
      {error && <p className="form-error-text" role="alert">{error}</p>}
      <div className="people-form__actions">
        <Button type="submit" variant="primary" size="sm" loading={saving} disabled={needsEmail && !email.trim()}>Create login</Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </form>
  );
};

export default GiveLoginPanel;
